import React, { useEffect, useMemo, useRef, useState } from "react"
import "./supplier-picker.scss"

// UI imports
import Badge from "../../../components/ui/badge/badge"
import { SearchIcon, TimesIcon } from "../../../utils/svgs"

// Data contract
import { supplierKey } from "../_utils/supplier-audit/types"

// Group order and labels for the dropdown, keyed on AuditSupplier.kind
const GROUPS = [
	{ kind: "hotel", heading: "Hotels", label: "Hotel", badge: "INFO" },
	{ kind: "course", heading: "Courses", label: "Course", badge: "POSITIVE" },
	{ kind: "transfer", heading: "Transfers", label: "Transfer", badge: "SPECIAL" },
]

// Most matches shown at once before asking the user to refine the search
const MAX_VISIBLE = 60

/**
 * Searchable picker for one or more suppliers out of the audit result. Filters
 * by name or email, lists matches grouped by supplier type, supports the
 * keyboard (up / down / enter / escape / backspace) and shows each chosen
 * supplier as a chip that can be removed again. Choosing a hotel and its
 * courses together is how a whole resort is packed into one workbook.
 *
 * @param {{ suppliers: import("../_utils/supplier-audit/types").AuditSupplierSummary[], value: string[], onChange: (keys: string[]) => void }} props
 */
export default function SupplierPicker({ suppliers, value, onChange }) {
	const [query, setQuery] = useState("")
	const [open, setOpen] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)

	const wrapperRef = useRef(null)
	const listRef = useRef(null)

	// The chosen suppliers, in the order they were picked
	const chosen = useMemo(() => new Set(value), [value])
	const selected = useMemo(() => value.map((key) => suppliers.find((supplier) => supplierKey(supplier) === key)).filter(Boolean), [suppliers, value])

	// Matches in group order, sorted by name within each group and capped
	const { matches, totalMatches } = useMemo(() => {
		const needle = query.trim().toLowerCase()
		const isMatch = (supplier) => !needle || supplier.name.toLowerCase().includes(needle) || (supplier.email || "").toLowerCase().includes(needle)

		const ordered = []
		GROUPS.forEach((group) => {
			suppliers
				.filter((supplier) => supplier.kind === group.kind && isMatch(supplier))
				.sort((a, b) => a.name.localeCompare(b.name))
				.forEach((supplier) => ordered.push(supplier))
		})

		return { matches: ordered.slice(0, MAX_VISIBLE), totalMatches: ordered.length }
	}, [suppliers, query])

	// Reset the highlighted row whenever the visible list changes
	useEffect(() => {
		setActiveIndex(0)
	}, [matches])

	// Keep the highlighted row in view while moving with the keyboard
	useEffect(() => {
		if (!open || !listRef.current) return
		const row = listRef.current.querySelector(".supplier-picker-option.is-active")
		if (row) row.scrollIntoView({ block: "nearest" })
	}, [activeIndex, open])

	// Close when clicking anywhere outside the picker
	useEffect(() => {
		const onMouseDown = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false)
		}
		document.addEventListener("mousedown", onMouseDown)
		return () => document.removeEventListener("mousedown", onMouseDown)
	}, [])

	// Toggle a supplier in or out of the selection. The list stays open and the
	// search is kept so a resort's hotel and courses can be picked one after
	// another from the same set of matches.
	const choose = (supplier) => {
		const key = supplierKey(supplier)
		onChange(chosen.has(key) ? value.filter((item) => item !== key) : [...value, key])
	}

	const remove = (key) => onChange(value.filter((item) => item !== key))

	const clear = (event) => {
		event.stopPropagation()
		onChange([])
		setQuery("")
		setOpen(false)
	}

	const onKeyDown = (event) => {
		if (event.key === "Escape") {
			setOpen(false)
			return
		}

		// Backspace on an empty search removes the last chip
		if (event.key === "Backspace" && query === "" && value.length > 0) {
			remove(value[value.length - 1])
			return
		}

		if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
			setOpen(true)
			return
		}

		if (event.key === "ArrowDown") {
			event.preventDefault()
			setActiveIndex((index) => Math.min(index + 1, matches.length - 1))
		} else if (event.key === "ArrowUp") {
			event.preventDefault()
			setActiveIndex((index) => Math.max(index - 1, 0))
		} else if (event.key === "Enter") {
			event.preventDefault()
			if (open && matches[activeIndex]) choose(matches[activeIndex])
		}
	}

	// Build the grouped view of the capped matches, keeping a flat index for
	// keyboard highlighting
	let flatIndex = -1
	const grouped = GROUPS.map((group) => ({
		...group,
		rows: matches.filter((supplier) => supplier.kind === group.kind).map((supplier) => ({ supplier, index: ++flatIndex })),
	})).filter((group) => group.rows.length > 0)

	return (
		<div
			ref={wrapperRef}
			className={["supplier-picker", open && "is-open", selected.length > 0 && "has-value"].filter(Boolean).join(" ")}>
			<div
				className="supplier-picker-input"
				onClick={() => wrapperRef.current?.querySelector("input")?.focus()}>
				<span className="supplier-picker-search-icon">
					<SearchIcon />
				</span>

				{selected.map((supplier) => {
					const key = supplierKey(supplier)
					const group = GROUPS.find((item) => item.kind === supplier.kind)

					return (
						<span
							key={key}
							className={`supplier-picker-chip is-${supplier.kind}`}
							title={group ? group.label : ""}>
							{supplier.name}
							<button
								type="button"
								className="supplier-picker-chip-remove"
								title="Remove"
								onMouseDown={(event) => event.preventDefault()}
								onClick={(event) => {
									event.stopPropagation()
									remove(key)
								}}>
								<TimesIcon />
							</button>
						</span>
					)
				})}

				<input
					type="text"
					className="supplier-picker-text"
					placeholder={selected.length > 0 ? "Add another supplier" : "Search suppliers by name or email"}
					value={query}
					onChange={(event) => {
						setQuery(event.target.value)
						setOpen(true)
					}}
					onFocus={() => setOpen(true)}
					onKeyDown={onKeyDown}
				/>

				{selected.length > 0 && (
					<button
						type="button"
						className="supplier-picker-clear"
						title="Clear all suppliers"
						onClick={clear}>
						<TimesIcon />
					</button>
				)}
			</div>

			{open && (
				<div
					ref={listRef}
					className="supplier-picker-dropdown">
					{matches.length === 0 && <div className="supplier-picker-empty">No suppliers match "{query.trim()}"</div>}

					{grouped.map((group) => (
						<div
							key={group.kind}
							className="supplier-picker-group">
							<div className="supplier-picker-heading">{group.heading}</div>

							{group.rows.map(({ supplier, index }) => {
								const key = supplierKey(supplier)
								const hasGap = !supplier.region || !supplier.country

								return (
									<div
										key={key}
										className={["supplier-picker-option", index === activeIndex && "is-active", chosen.has(key) && "is-selected"].filter(Boolean).join(" ")}
										onMouseEnter={() => setActiveIndex(index)}
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => choose(supplier)}>
										<span className="supplier-picker-name">{supplier.name}</span>
										<Badge
											type={group.badge}
											label={group.label}
										/>
										<span className="supplier-picker-count">
											{supplier.bookingCount.toLocaleString()} booking{supplier.bookingCount === 1 ? "" : "s"}
										</span>
										{hasGap && (
											<span
												className="supplier-picker-warning"
												title="Region or country is missing for this supplier">
												!
											</span>
										)}
									</div>
								)
							})}
						</div>
					))}

					{totalMatches > MAX_VISIBLE && (
						<div className="supplier-picker-note">
							Showing {MAX_VISIBLE} of {totalMatches.toLocaleString()} matches. Refine your search to see the rest.
						</div>
					)}
				</div>
			)}
		</div>
	)
}
